import React, { useContext, useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import { NavLink, useLocation } from 'react-router-dom';
import { AppSettings } from './../../config/app-settings.js';
import menus from './../../config/app-menu.jsx';
import { slideDown } from './../../composables/slideDown.js';
import { slideUp } from './../../composables/slideUp.js';

function isMenuActive(path, pathname) {
  return pathname === path || pathname.startsWith(`${path}/`);
}

function filterMenu(menu, term) {
  if (!term) return menu;
  const titleMatches = menu.title.toLowerCase().includes(term);
  const children = menu.children?.map((child) => filterMenu(child, term)).filter(Boolean);
  if (!titleMatches && !children?.length) return null;
  return children?.length ? { ...menu, children } : { ...menu, children: undefined };
}

function NavItem({ menu }) {
  const location = useLocation();
  const active = !menu.disabled && isMenuActive(menu.path, location.pathname);
  const [expanded, setExpanded] = useState(false);
  const [closed, setClosed] = useState(false);
  const [transition, setTransition] = useState('');

  useEffect(() => {
    if (active) setClosed(false);
  }, [active]);

  const icon = menu.icon ? <div className="menu-icon"><Icon icon={menu.icon} /></div> : null;
  const image = menu.img ? <div className="menu-icon-img"><img src={menu.img} alt="" /></div> : null;
  const label = menu.label ? <span className="menu-label">{menu.label}</span> : null;
  const badge = menu.badge ? <div className="menu-badge">{menu.badge}</div> : null;
  const highlight = menu.highlight ? <i className="fa fa-paper-plane text-theme" /> : null;
  const title = <div className="menu-text">{menu.title} {label} {highlight}</div>;

  const toggle = (event) => {
    if (menu.disabled) {
      event.preventDefault();
      return;
    }
    if (!menu.children) return;
    event.preventDefault();
    const item = event.currentTarget.closest('.menu-item');
    const target = item?.querySelector(':scope > .menu-submenu');
    if (!item || !target) return;

    item.parentElement?.querySelectorAll(':scope > .menu-item.has-sub').forEach((sibling) => {
      if (sibling === item) return;
      const submenu = sibling.querySelector(':scope > .menu-submenu');
      if (!submenu) return;
      const isOpen = sibling.classList.contains('expand') || (sibling.classList.contains('active') && !sibling.classList.contains('closed'));
      if (isOpen) slideUp(submenu, 250);
      sibling.classList.remove('expand', 'expanding');
      sibling.classList.add('closed');
    });

    const isOpen = item.classList.contains('expand') || (item.classList.contains('active') && !item.classList.contains('closed'));
    setTransition(isOpen ? 'closing' : 'expanding');
    setExpanded(!isOpen);
    setClosed(isOpen);
    if (isOpen) {
      slideUp(target, 250);
    } else {
      slideDown(target, 250);
    }
    window.setTimeout(() => setTransition(''), 250);
  };

  const className = `menu-item${active ? ' active' : ''}${menu.disabled ? ' disabled' : ''}${menu.children ? ' has-sub' : ''}${expanded ? ' expand' : ''}${closed ? ' closed' : ''}${transition ? ` ${transition}` : ''}`;
  const link = menu.disabled ? (
    <a href="#/" className="menu-link" onClick={toggle}>{image} {icon} {title} {badge}</a>
  ) : menu.external ? (
    <a href={menu.path} target="_blank" rel="noreferrer" className="menu-link">{image} {icon} {title} {badge}</a>
  ) : (
    <NavLink className="menu-link" to={menu.path}>{image} {icon} {title} {badge}</NavLink>
  );

  return (
    <div className={className}>
      {menu.children ? (
        <a href="#/" className="menu-link" onClick={toggle}>
          {image} {icon} {title} {!menu.badge && <div className="menu-caret" />} {badge}
        </a>
      ) : (
        link
      )}
      {menu.children && (
        <div className="menu-submenu">
          {menu.children.map((child) => <NavItem key={child.path} menu={child} />)}
        </div>
      )}
    </div>
  );
}

function SidebarNav() {
  const { appSidebarSearch } = useContext(AppSettings);
  const [search, setSearch] = useState('');

  return (
    <>
      {appSidebarSearch && (
        <div className="menu-search mt-3">
          <input
            type="text"
            className="form-control bg-light border-0 shadow-none fw-semibold"
            placeholder="Filter menu..."
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
      )}
      <div className="menu-header">Navigation</div>
      {menus.map((menu) => filterMenu(menu, search.toLowerCase())).filter(Boolean).map((menu) => (
        menu.menuHeader
          ? <div key={menu.path} className="menu-header">{menu.title}</div>
          : <NavItem key={menu.path} menu={menu} />
      ))}
    </>
  );
}

export default SidebarNav;
